import { useEffect, useState } from 'react';
import { getMonthlyUsageCountByUserIdRealTime } from '../api/firebase/firestore/company/read';
import { getUser } from '../api/firebase/firestore/user/read';
import { getUserMonthlyOcrLimit, getUserMonthlyOcrLimitMessage } from '../utils/userUsageLimit';
import type { UserType } from '../types/user';

/**
 * 個人別 月間OCR上限の状態（#3133）
 */
export interface UserMonthlyOcrLimitState {
  /** 月間上限ページ数（null=無制限 / 未取得） */
  monthlyLimit: number | null;
  /** 当月の利用済みページ数 */
  usedPages: number;
  /**
   * 指定ページ数を追加した場合の超過メッセージを返す。
   * 超過しない場合・無制限の場合は null。
   */
  getLimitMessage: (addPages: number) => string | null;
}

/**
 * 個人別 月間OCR上限をリアルタイム監視するフック（#3133）
 *
 * 従来は上限チェックがアップロード実行時にしかなく、押下して初めてエラーが出ていた。
 * 上限と当月利用量を事前に取得しておくことで、会社枠と同じくファイル選択時点で
 * インラインのバリデーションエラーを表示できるようにする。
 *
 * 上限値は AuthContext の user ではなく Firestore から直接取得する。
 * AuthContext の UserSetType は usage_limit を保持しておらず、
 * それを参照すると常に「無制限」と誤判定されるため。
 *
 * 代理モード中は個人別上限の対象外（カウントされない）のため監視しない。
 *
 * @param userId 実行ユーザーのUID
 * @param companyId 対象企業ID
 * @param isProxyMode 代理モードか
 * @returns 上限・利用量とメッセージ生成関数
 */
export const useUserMonthlyOcrLimit = (
  userId: string | null | undefined,
  companyId: string | null | undefined,
  isProxyMode: boolean,
): UserMonthlyOcrLimitState => {
  const [monthlyLimit, setMonthlyLimit] = useState<number | null>(null);
  const [usedPages, setUsedPages] = useState<number>(0);

  // 上限値を Firestore のユーザードキュメントから取得する。
  // 代理モード中は個人別上限の対象外のため取得しない。
  useEffect(() => {
    let isActive = true;

    if (isProxyMode || !userId) {
      setMonthlyLimit(null);
      return;
    }

    const fetchLimit = async (): Promise<void> => {
      const userDoc = await getUser(userId);
      if (!isActive) return;
      setMonthlyLimit(getUserMonthlyOcrLimit((userDoc as UserType | undefined) ?? null));
    };
    void fetchLimit();

    return () => {
      isActive = false;
    };
  }, [userId, isProxyMode]);

  // 当月の利用量を監視（上限未設定＝無制限の場合は監視不要）
  useEffect(() => {
    if (monthlyLimit === null || !companyId || !userId) {
      setUsedPages(0);
      return;
    }

    const unsubscribe = getMonthlyUsageCountByUserIdRealTime(companyId, userId, count => {
      setUsedPages(count);
    });

    return () => {
      unsubscribe();
    };
  }, [monthlyLimit, companyId, userId]);

  const getLimitMessage = (addPages: number): string | null =>
    getUserMonthlyOcrLimitMessage(monthlyLimit, usedPages, addPages);

  return { monthlyLimit, usedPages, getLimitMessage };
};
